import { action, computed, IReactionDisposer, makeObservable, observable, reaction } from 'mobx';

import { TREE_ROOT_NODE_ID, TreeNodeFolder } from '@/entities/Tree';
import { DocumentsTreeSelectionStore } from '@/stores/local/shared/DocumentsTreeSelectionStore/DocumentsTreeSelectionStore';
import { PopupStore } from '@/stores/local/shared/PopupStore';
import { TreeStore } from '@/stores/local/shared/TreeStore';

import { CreateFolderFormStore } from './CreateFolderFormStore';

type CreateFolderPopupStoreParams = {
  getOrganizationId: () => number;
  treeStore: TreeStore;
  selectionStore: DocumentsTreeSelectionStore;
};

type CreateFolderOptions = {
  requireDateFormat?: boolean;
  parent?: TreeNodeFolder | null;
};

export class CreateFolderPopupStore extends PopupStore<TreeNodeFolder | null> {
  readonly form: CreateFolderFormStore;
  readonly treeStore: TreeStore;
  private readonly _selectionStore: DocumentsTreeSelectionStore;
  private _parentFolder: TreeNodeFolder | null = null;
  private _selectionDisposer?: IReactionDisposer;

  isParentSelectable = false;

  constructor({ getOrganizationId, treeStore, selectionStore }: CreateFolderPopupStoreParams) {
    super();
    this.treeStore = treeStore;
    this._selectionStore = selectionStore;
    this.form = new CreateFolderFormStore({
      getOrganizationId,
      treeStore,
      onSuccess: () => this.close(),
    });

    makeObservable<this, '_parentFolder'>(this, {
      isParentSelectable: observable,
      _parentFolder: observable.ref,
      parentFolder: computed,
      openForCreate: action.bound,
      openForEdit: action.bound,
    });
  }

  changeOrganizationId(): void {
    this.close();
    this.form.clearContext();
    this.form.reset();
    this.form.meta.reset();
  }

  openWithNull() {
    this.isParentSelectable = true;
    this._parentFolder = null;
    this._selectionStore.clear();
    this.form.setParentId(null);
    this.form.setEntity(null);
    super.openWithNull();
    this._setupSelectionSync();
  }

  openForCreate(parentId: string | null, options?: CreateFolderOptions) {
    this.isParentSelectable = false;
    this._stopSelectionSync();
    this._parentFolder = this._resolveParentFolder(parentId, options?.parent ?? null);
    this.form.setParentId(parentId, options);
    this.form.setEntity(null);
    super.open(null);
  }

  openForEdit(entity: TreeNodeFolder, parentId: string | null, options?: CreateFolderOptions) {
    this.isParentSelectable = false;
    this._stopSelectionSync();
    this._parentFolder = this._resolveParentFolder(parentId, options?.parent ?? null);
    this.form.setParentId(parentId, options);
    this.form.setEntity(entity);
    super.open(entity);
  }

  close() {
    super.close();
    this.isParentSelectable = false;
    this._parentFolder = null;
    this._stopSelectionSync();
    this.form.clearContext();
    this.form.reset();
  }

  get parentFolder(): TreeNodeFolder | null {
    return this._parentFolder;
  }

  private _resolveParentFolder(parentId: string | null, fallback: TreeNodeFolder | null) {
    if (fallback) {
      return fallback;
    }

    if (!parentId || parentId === TREE_ROOT_NODE_ID) {
      return this.treeStore.data.type === 'folder' ? (this.treeStore.data as TreeNodeFolder) : null;
    }

    return this._findFolderInTree(parentId);
  }

  private _findFolderInTree(targetId: string): TreeNodeFolder | null {
    const stack: TreeStore[] = [this.treeStore];

    while (stack.length) {
      const current = stack.pop()!;

      if (current.data.type === 'folder' && current.data.id === targetId) {
        return current.data as TreeNodeFolder;
      }

      if (current.children?.length) {
        stack.push(...current.children);
      }
    }

    return null;
  }

  private _setupSelectionSync(): void {
    this._selectionStore.setSelectAll(false);
    this._selectionDisposer?.();

    this._selectionDisposer = reaction(
      () => this._selectionStore.singleFolderToServerFormat ?? null,
      (folderId) => {
        if (!this.isParentSelectable) {
          return;
        }

        const normalized = folderId ?? null;
        const resolvedParent = this._resolveParentFolder(normalized, null);

        if (this.form.parentId !== normalized) {
          this.form.setParentId(normalized);
        }

        this._parentFolder = resolvedParent;
      },
      {
        fireImmediately: true,
      },
    );
  }

  private _stopSelectionSync(): void {
    if (this._selectionDisposer) {
      this._selectionDisposer();
      this._selectionDisposer = undefined;
    }
  }

  destroy(): void {
    super.destroy();
    this.form.destroy();
  }
}
